import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import styles from "./PromotionDetail.module.scss";
import { Card, Row, Col, Table, Tag, Button, Typography, Space } from "antd";
import { ArrowLeftOutlined, ArrowUpOutlined, ArrowDownOutlined, ShoppingCartOutlined, DollarOutlined, FlagOutlined, LineChartOutlined, ShopOutlined, TrophyOutlined, AlertOutlined, BulbOutlined, WarningOutlined, SwapOutlined } from "@ant-design/icons";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title as CTitle, Legend, Tooltip as CTooltip, Filler } from "chart.js";
import { allPromos } from "./PromotionAnalysis";
import type { Promo } from "./PromotionAnalysis";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, CTitle, Legend, CTooltip, Filler);

const { Title, Text } = Typography;
const fmt = (v: number) => `$${v.toLocaleString("es-AR", { maximumFractionDigits: 0 })}`;
const fmtN = (v: number) => v.toLocaleString("es-AR");

const Vr: React.FC<{ value: number; suffix?: string }> = ({ value, suffix = "%" }) => {
  const pos = value >= 0;
  return (<span className={`${styles.variation} ${pos ? styles.positive : styles.negative}`}>{pos ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {pos ? "+" : ""}{value.toFixed(1)}{suffix}</span>);
};

const VENTA_GLOBAL = 54400000;

const channelPerf = [
  { key: 1, canal: "Retail", presencia: "Sí", stock: 1200, venta: 2800000, unidades: 1900, tickets: 1400, margen: 44.2, pctVentaCanal: 58.3, ventaProy: 3000000 },
  { key: 2, canal: "eCommerce", presencia: "Sí", stock: 800, venta: 1600000, unidades: 1100, tickets: 900, margen: 46.8, pctVentaCanal: 33.3, ventaProy: 1800000 },
  { key: 3, canal: "Marketplace", presencia: "No", stock: 0, venta: 0, unidades: 0, tickets: 0, margen: 0, pctVentaCanal: 0, ventaProy: 0 },
  { key: 4, canal: "Outlet", presencia: "Sí", stock: 300, venta: 400000, unidades: 600, tickets: 300, margen: 32.5, pctVentaCanal: 8.3, ventaProy: 420000 },
];

const storePerf = [
  { key: 1, tienda: "Tienda Central", presencia: "Sí", stock: 480, venta: 1400000, unidades: 950, tickets: 680, margen: 45.2, pctVentaTienda: 29.2, ventaProy: 1500000 },
  { key: 2, tienda: "Sucursal Norte", presencia: "Sí", stock: 320, venta: 800000, unidades: 540, tickets: 400, margen: 43.8, pctVentaTienda: 16.7, ventaProy: 850000 },
  { key: 3, tienda: "Tienda Online", presencia: "Sí", stock: 800, venta: 1600000, unidades: 1100, tickets: 900, margen: 46.8, pctVentaTienda: 33.3, ventaProy: 1800000 },
  { key: 4, tienda: "Outlet Norte", presencia: "Sí", stock: 300, venta: 400000, unidades: 600, tickets: 300, margen: 32.5, pctVentaTienda: 8.3, ventaProy: 420000 },
  { key: 5, tienda: "Sucursal Sur", presencia: "Sí", stock: 200, venta: 600000, unidades: 410, tickets: 320, margen: 44.0, pctVentaTienda: 12.5, ventaProy: 640000 },
];

const evolution = [
  { key: 1, periodo: "Pre-promo (S-2)", venta: 1100000, unidades: 720, margen: 46.5, lift: 0, lectura: "Baseline" },
  { key: 2, periodo: "Pre-promo (S-1)", venta: 1150000, unidades: 750, margen: 46.2, lift: 0, lectura: "Baseline" },
  { key: 3, periodo: "Semana 1", venta: 1800000, unidades: 1200, margen: 42.8, lift: 58.7, lectura: "Pico de arranque" },
  { key: 4, periodo: "Semana 2", venta: 1600000, unidades: 1050, margen: 43.5, lift: 41.2, lectura: "Sostenido" },
  { key: 5, periodo: "Semana 3", venta: 1400000, unidades: 920, margen: 44.8, lift: 23.5, lectura: "Desaceleración" },
  { key: 6, periodo: "Post-promo (S+1)", venta: 980000, unidades: 640, margen: 47.1, lift: -13.6, lectura: "Efecto rebote" },
  { key: 7, periodo: "Post-promo (S+2)", venta: 1080000, unidades: 700, margen: 46.8, lift: -4.7, lectura: "Recuperación" },
];

const whereWorked = [
  { key: 1, tienda: "Tienda Central", lift: 62.4, margen: 45.2, roi: 3.2, lectura: "Muy positivo" },
  { key: 2, tienda: "Tienda Online", lift: 48.8, margen: 46.8, roi: 2.8, lectura: "Muy positivo" },
  { key: 3, tienda: "Sucursal Norte", lift: 35.2, margen: 43.8, roi: 1.8, lectura: "Bueno" },
  { key: 4, tienda: "Sucursal Sur", lift: 22.1, margen: 44.0, roi: 1.2, lectura: "Bueno" },
  { key: 5, tienda: "Outlet Norte", lift: 8.4, margen: 32.5, roi: 0.6, lectura: "Negativo" },
];

const products = [
  { key: 1, producto: "Zapatilla Aero Max 2026", venta: 2100000, unidades: 1400, margen: 44.2, pctAporte: 43.8 },
  { key: 2, producto: "Zapatilla Asfalto Pro", venta: 1200000, unidades: 820, margen: 43.5, pctAporte: 25.0 },
  { key: 3, producto: "Zapatilla Trail Runner X", venta: 800000, unidades: 540, margen: 46.5, pctAporte: 16.7 },
  { key: 4, producto: "Medias Running Pack x3", venta: 400000, unidades: 2200, margen: 52.1, pctAporte: 8.3 },
  { key: 5, producto: "Zapatilla Running Kid Pro", venta: 300000, unidades: 260, margen: 42.1, pctAporte: 6.3 },
];

const competing = [
  { key: 1, promo: "2da unidad 50% Off", categoria: "Zapatillas Running", periodo: "Solapamiento parcial", canal: "Retail + eCommerce" },
  { key: 2, promo: "20% Off Indumentaria", categoria: "Indumentaria (cross)", periodo: "Mismo período", canal: "Retail" },
];

const detailInsights = [
  "Pendiente: implementar motor de insights automáticos por promoción.",
];

const alerts = [
  "Pendiente: implementar motor de alertas por promoción.",
];

const chartLabels = evolution.map((e) => e.periodo);
const chartData = {
  labels: chartLabels,
  datasets: [
    { label: "Venta", data: evolution.map((e) => e.venta), borderColor: "#8b5cf6", backgroundColor: "rgba(139,92,246,0.08)", fill: true, tension: 0.3, pointRadius: 4, borderWidth: 2 },
  ],
};
const chartOpts = {
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { position: "top" as const, labels: { usePointStyle: true, font: { size: 12 } } } },
  scales: { y: { beginAtZero: false, grid: { color: "rgba(0,0,0,0.04)" } }, x: { grid: { display: false }, ticks: { font: { size: 10 } } } },
};

const perfCols = (entityKey: string, pctKey: string) => [
  { title: entityKey === "canal" ? "Canal" : "Tienda", dataIndex: entityKey, key: entityKey },
  { title: "Presencia", dataIndex: "presencia", key: "presencia", render: (v: string) => <Tag color={v === "Sí" ? "green" : "default"}>{v}</Tag> },
  { title: "Stock", dataIndex: "stock", key: "stock", render: (v: number) => fmtN(v), align: "right" as const },
  { title: "Venta", dataIndex: "venta", key: "venta", render: (v: number) => fmt(v), align: "right" as const },
  { title: "Unidades", dataIndex: "unidades", key: "unidades", render: (v: number) => fmtN(v), align: "right" as const },
  { title: "Tickets", dataIndex: "tickets", key: "tickets", render: (v: number) => fmtN(v), align: "right" as const },
  { title: "Ticket prom.", key: "tp", render: (_: any, r: any) => r.tickets > 0 ? fmt(Math.round(r.venta / r.tickets)) : "—", align: "right" as const },
  { title: "Margen", dataIndex: "margen", key: "margen", render: (v: number) => v > 0 ? `${v.toFixed(1)}%` : "—", align: "right" as const },
  { title: `% ${entityKey}`, dataIndex: pctKey, key: pctKey, render: (v: number) => `${v.toFixed(1)}%`, align: "right" as const },
  { title: "Venta proy.", dataIndex: "ventaProy", key: "ventaProy", render: (v: number) => fmt(v), align: "right" as const },
];

const PromotionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const p: Promo = allPromos.find((x) => x.id === Number(id)) || allPromos[0];

  const incremental = p.ventaAtribuida - p.ventaBaseline;
  const lift = p.ventaBaseline > 0 ? (incremental / p.ventaBaseline) * 100 : 0;
  const margen = p.ventaAtribuida > 0 ? ((p.ventaAtribuida - p.costo) / p.ventaAtribuida) * 100 : 0;
  const roi = p.costoPromo > 0 ? incremental / p.costoPromo : 0;
  const pctGlobal = (p.ventaAtribuida / VENTA_GLOBAL) * 100;

  const decision = roi > 2 && margen > 40 ? "Repetir" : roi >= 1 ? "Ajustar" : "Evitar";
  const decisionColor = decision === "Repetir" ? "green" : decision === "Ajustar" ? "orange" : "red";

  return (
    <div className={styles.wrapper}>
      <Button icon={<ArrowLeftOutlined />} type="text" className={styles.backBtn}
        onClick={() => navigate("/commercial-analysis/promotions")}>Promociones</Button>

      {/* HEADER */}
      <Card className={styles.headerCard}>
        <Title level={3} className={styles.pageTitle}>{p.nombre}</Title>
        <Space wrap size="middle" style={{ marginTop: 8 }}>
          <Tag color="purple">{p.tipo}</Tag>
          <Text type="secondary">Ámbito: {p.ambito}</Text>
          <Text type="secondary">Precio medio: $3.400</Text>
          <Text type="secondary">Costo medio: $1.870</Text>
        </Space>
      </Card>

      {/* KPIs + DECISION */}
      <div className={styles.kpiRow}>
        {[
          { label: "Venta atribuida", value: fmt(p.ventaAtribuida) },
          { label: "Venta incremental", value: fmt(incremental) },
          { label: "Margen", value: `${margen.toFixed(1)}%` },
          { label: "ROI", value: `${roi.toFixed(1)}x` },
          { label: "% venta global", value: `${pctGlobal.toFixed(1)}%` },
        ].map((k, i) => (
          <Card className={styles.kpiCard} key={i}>
            <Text className={styles.kpiLabel}>{k.label}</Text>
            <div className={styles.kpiValue}>{k.value}</div>
          </Card>
        ))}
        <Card className={styles.kpiCard} style={{ borderLeft: `4px solid ${decisionColor === "green" ? "#52c41a" : decisionColor === "orange" ? "#faad14" : "#f5222d"}` }}>
          <Text className={styles.kpiLabel}>Decisión sugerida</Text>
          <Tag color={decisionColor} style={{ fontSize: 16, padding: "4px 12px", marginTop: 4 }}>{decision}</Tag>
        </Card>
      </div>

      {/* CHART */}
      <Card className={styles.sectionCard} title={<Space><LineChartOutlined /><span>Evolución de venta</span></Space>}>
        <div className={styles.chartContainer}><Line data={chartData} options={chartOpts} /></div>
        <Table columns={[
          { title: "Período", dataIndex: "periodo", key: "periodo" },
          { title: "Venta", dataIndex: "venta", key: "venta", render: (v: number) => fmt(v), align: "right" as const },
          { title: "Unidades", dataIndex: "unidades", key: "unidades", render: (v: number) => fmtN(v), align: "right" as const },
          { title: "Margen", dataIndex: "margen", key: "margen", render: (v: number) => `${v.toFixed(1)}%`, align: "right" as const },
          { title: "Lift", dataIndex: "lift", key: "lift", render: (v: number) => v !== 0 ? <Vr value={v} /> : "—", align: "right" as const },
          { title: "Lectura", dataIndex: "lectura", key: "lectura", render: (v: string) => <Tag>{v}</Tag> },
        ]} dataSource={evolution} pagination={false} size="small" className={styles.dataTable} style={{ marginTop: 16 }} />
      </Card>

      {/* PERF CANAL / TIENDA */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card className={styles.sectionCard} title={<Space><ShopOutlined /><span>Performance por canal</span></Space>}>
            <Table columns={perfCols("canal", "pctVentaCanal") as any} dataSource={channelPerf} pagination={false} size="small" scroll={{ x: 900 }} className={styles.dataTable} />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card className={styles.sectionCard} title={<Space><ShopOutlined /><span>Performance por tienda</span></Space>}>
            <Table columns={perfCols("tienda", "pctVentaTienda") as any} dataSource={storePerf} pagination={false} size="small" scroll={{ x: 900 }} className={styles.dataTable} />
          </Card>
        </Col>
      </Row>

      {/* WHERE WORKED */}
      <Card className={styles.sectionCard} title={<Space><TrophyOutlined /><span>Dónde funcionó y dónde no</span></Space>}>
        <Table columns={[
          { title: "Tienda", dataIndex: "tienda", key: "tienda" },
          { title: "Lift", dataIndex: "lift", key: "lift", render: (v: number) => <Vr value={v} />, align: "right" as const },
          { title: "Margen", dataIndex: "margen", key: "margen", render: (v: number) => `${v.toFixed(1)}%`, align: "right" as const },
          { title: "ROI", dataIndex: "roi", key: "roi", render: (v: number) => `${v.toFixed(1)}x`, align: "right" as const },
          { title: "Lectura", dataIndex: "lectura", key: "lectura", render: (v: string) => <Tag color={v === "Muy positivo" ? "green" : v === "Bueno" ? "blue" : "red"}>{v}</Tag> },
        ]} dataSource={whereWorked} pagination={false} size="small" className={styles.dataTable} />
      </Card>

      {/* PRODUCTS */}
      <Card className={styles.sectionCard} title={<Space><ShoppingCartOutlined /><span>Productos dentro de la promoción</span></Space>}>
        <Table columns={[
          { title: "Producto", dataIndex: "producto", key: "producto" },
          { title: "Venta atribuida", dataIndex: "venta", key: "venta", render: (v: number) => fmt(v), align: "right" as const },
          { title: "Unidades", dataIndex: "unidades", key: "unidades", render: (v: number) => fmtN(v), align: "right" as const },
          { title: "Margen", dataIndex: "margen", key: "margen", render: (v: number) => `${v.toFixed(1)}%`, align: "right" as const },
          { title: "% aporte", dataIndex: "pctAporte", key: "pctAporte", render: (v: number) => `${v.toFixed(1)}%`, align: "right" as const },
        ]} dataSource={products} pagination={false} size="small" className={styles.dataTable} />
      </Card>

      {/* COMPETING + ALERTS + INSIGHTS */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={8}>
          <Card className={styles.sectionCard} title={<Space><SwapOutlined /><span>Promociones que compitieron</span></Space>}>
            <Table columns={[
              { title: "Promoción", dataIndex: "promo", key: "promo" },
              { title: "Categoría", dataIndex: "categoria", key: "categoria" },
              { title: "Período", dataIndex: "periodo", key: "periodo" },
              { title: "Canal", dataIndex: "canal", key: "canal" },
            ]} dataSource={competing} pagination={false} size="small" className={styles.dataTable} />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card className={styles.sectionCard} title={<Space><AlertOutlined style={{ color: "#f5222d" }} /><span>Alertas</span></Space>}>
            <div className={styles.bulletList}>{alerts.map((a, i) => (<div key={i} className={styles.bulletItem}><AlertOutlined className={styles.bRed} /><Text type="secondary">{a}</Text></div>))}</div>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card className={styles.sectionCard} title={<Space><BulbOutlined style={{ color: "#faad14" }} /><span>Insights</span></Space>}>
            <div className={styles.bulletList}>{detailInsights.map((ins, i) => (<div key={i} className={styles.bulletItem}><BulbOutlined className={styles.bYellow} /><Text type="secondary">{ins}</Text></div>))}</div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PromotionDetail;
